/**
 * Friend Stats Prediction Helper
 * Handles prediction display logic and outcome checking
 */

export interface PredictionResult {
  text: string;
  odds: number | null;
  isCorrect: boolean | null; // null = game not finished, true = correct, false = wrong
}

/**
 * Get match result from scores
 */
export const getMatchResult = (
  homeScore: number | null,
  awayScore: number | null
): 'home' | 'away' | 'draw' | null => {
  if (homeScore === null || awayScore === null) return null;
  
  if (homeScore > awayScore) return 'home';
  if (awayScore > homeScore) return 'away';
  return 'draw';
};

/**
 * Get prediction display text and check correctness
 */
export const getPredictionDisplay = (
  outcome: 'home' | 'away' | 'draw' | null,
  homeTeam: string,
  awayTeam: string,
  odds: { home: number; draw: number | null; away: number } | null,
  matchResult: 'home' | 'away' | 'draw' | null,
  isFinished: boolean
): PredictionResult => {
  if (!outcome) {
    return { text: 'No pick', odds: null, isCorrect: null };
  }

  const normalizedOutcome = outcome.toLowerCase();
  let text = '';
  let predictionOdds = null;

  if (normalizedOutcome === 'home') {
    text = homeTeam;
    predictionOdds = odds?.home || null;
  } else if (normalizedOutcome === 'away') {
    text = awayTeam;
    predictionOdds = odds?.away || null;
  } else if (normalizedOutcome === 'draw') {
    text = 'Draw';
    predictionOdds = odds?.draw || null;
  } else {
    return { text: 'No pick', odds: null, isCorrect: null };
  }

  // Only check correctness if game is finished
  const isCorrect = isFinished && matchResult ? normalizedOutcome === matchResult : null;

  return { text, odds: predictionOdds, isCorrect };
};

/**
 * Get short outcome label (1/X/2) for display
 */
export const getOutcomeShort = (outcome: string | null): string => {
  if (!outcome) return '-';
  const normalized = outcome.toLowerCase();
  if (normalized === 'home') return '1';
  if (normalized === 'draw') return 'X';
  if (normalized === 'away') return '2';
  return '-';
};

